import { useCallback, useMemo } from "react";
import TwoColumnLayout from "@/components/Layout/TwoColumnLayout";
import {
  exportMediaResultsToJSON,
  exportMediaResultsToCSV,
} from "@/features/keyword-monitoring/utils";
import { TAB_ORDER } from "../../config";
import MediaStepperPage from "./SourceMediaStepperPage";
import SelectedSources from "./SourceSelectedSources";
import { useSourceContainer } from "../../hooks/useSourceContainer";

export default function SourceContainer() {
  const {
    data,
    isLoading,
    error,
    showResults,
    hasLaunched,
    isEmptyResults,
    responseJSON,
    apiEndpoint,
    totalResults,
    currentTabIndex,
    isFirstTab,
    isLastTab,
    selectedToken,
    effectiveToken,
    isApiTokenLoading,
    availableTokens,
    tokensLoading,
    handleShowResults,
    handleLaunch,
    handleCopyCode,
    handleTokenSelect,
    handlePanelResize,
    handleEditSources,
    handleNext,
    handleBack,
    formatTabLabel,
    monitoringType,
  } = useSourceContainer();

  const handleExportJSON = useCallback(() => {
    exportMediaResultsToJSON(data, "media-results.json");
  }, [data]);

  const handleExportCSV = useCallback(() => {
    exportMediaResultsToCSV(data, "media-results.csv");
  }, [data]);

  const content = useMemo(() => {
    if (hasLaunched) {
      return (
        <div className="flex h-full min-h-0 flex-col">
          <SelectedSources
            onLaunch={handleLaunch}
            onEditSources={handleEditSources}
            selectedToken={selectedToken}
          />
        </div>
      );
    }

    return (
      <MediaStepperPage
        navigationProps={{
          isFirstTab,
          isLastTab,
          nextLabel: formatTabLabel(TAB_ORDER[currentTabIndex + 1]),
          onNext: handleNext,
          onBack: handleBack,
          onLaunch: handleLaunch,
        }}
        selectedToken={selectedToken}
      />
    );
  }, [
    hasLaunched,
    handleLaunch,
    handleEditSources,
    selectedToken,
    isFirstTab,
    isLastTab,
    formatTabLabel,
    currentTabIndex,
    handleNext,
    handleBack,
  ]);

  return (
    <TwoColumnLayout
      data={data}
      isLoading={isLoading}
      error={error}
      isEmptyResults={isEmptyResults}
      responseJSON={responseJSON}
      apiEndpoint={apiEndpoint}
      apiToken={effectiveToken}
      isApiTokenLoading={isApiTokenLoading}
      resultsTitle="Results"
      resultsDescription={`Found ${totalResults} results matching your filter.`}
      showResults={showResults}
      onShowResults={handleShowResults}
      onLaunch={handleLaunch}
      onCopyCode={handleCopyCode}
      handleExportCSV={handleExportCSV}
      handleExportJSON={handleExportJSON}
      floatingButtonCondition={hasLaunched}
      leftPanelMinSize={50}
      rightPanelMinSize={30}
      onPanelResize={handlePanelResize}
      showTokenDropdown={true}
      onTokenSelect={handleTokenSelect}
      monitoringType={monitoringType}
      selectedToken={selectedToken}
      availableTokens={availableTokens}
      tokensLoading={tokensLoading}
    >
      {content}
    </TwoColumnLayout>
  );
}
